import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from "../../provider/AuthProvider";
import { useNavigate, useLocation } from "react-router-dom";
import api, { logout as apiLogout } from '../../api/api';
import useTranslation from '../../hooks/useTranslation';
import StatusSelector from './StatusSelector';
import GlobalTimer from '../../components/GlobalTimer';
import { useInstalledApps } from '../../provider/InstalledAppsProvider';

import HelpCenter from './HelpCenter';
import { useTour } from '../../components/tour';

import bellIcon from '../assets/icons/bell_icon.svg';
import questionIcon from '../assets/icons/question_Icon.svg';
import dropdownIcon from '../assets/icons/dropdown_icon.svg';
import './Header.css';
import { visibleInterval } from '../../utils/visibleInterval';

const Header = () => {
  const { userdetails } = useAuth();
  const { t, language, setLanguage, dir, languages } = useTranslation();
  const { isAppInstalled } = useInstalledApps();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isConfigMenuOpen, setIsConfigMenuOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [searchValue, setSearchValue] = useState(searchParams.get('q') || searchParams.get('search') || "");

  // Global Search States
  const [searchResults, setSearchResults] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const searchContainerRef = useRef(null);
  const langDropdownRef = useRef(null);

  // Quick Create States
  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
  const quickCreateRef = useRef(null);

  // ── Notification Center State ─────────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifLoading, setNotifLoading] = useState(false);
  const notifPanelRef = useRef(null);
  const { token } = useAuth();

  // Fetch unread count (poll every 30s)
  useEffect(() => {
    if (!token) return;
    const fetchCount = async () => {
      try {
        const { data } = await api.get('/notifications/count/');
        setUnreadCount(data.unread || 0);
      } catch (_) { }
    };
    fetchCount();
    const interval = visibleInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [token]);

  // Close notif panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openNotifications = async () => {
    setNotifOpen(o => !o);
    if (notifOpen) return;
    setNotifLoading(true);
    try {
      const { data } = await api.get('/notifications/');
      setNotifications(data.notifications || []);
      setUnreadCount(0); // optimistic clear
    } catch (_) { }
    finally { setNotifLoading(false); }
  };

  const markRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read/`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    } catch (_) { }
  };

  const markAllRead = async () => {
    try {
      await api.post('/notifications/read-all/');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (_) { }
  };

  const dismiss = async (id, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/notifications/${id}/`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (_) { }
  };

  const handleNotifClick = (notif) => {
    markRead(notif.id);
    if (notif.entity_url) {
      setNotifOpen(false);
      navigate(notif.entity_url);
    }
  };

  const relativeTime = (iso) => {
    const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const query = params.get('q') || params.get('search');
    if (query !== null) {
      setSearchValue(query);
    } else {
      setSearchValue("");
    }
  }, [location.search]);

  // Debounced Global Search for recommendations
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      const trimmed = searchValue.trim();
      if (trimmed.length >= 2) {
        setIsSearching(true);
        setShowSearchDropdown(true);
        try {
          // Fixed URL: should be /common/search/ if baseURL is /api
          const { data } = await api.get(`/common/search/?q=${trimmed}`);
          setSearchResults(data);
        } catch (error) {
          console.error('Global search error:', error);
          setSearchResults({});
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults({});
        setShowSearchDropdown(false);
      }
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [searchValue]);

  const triggerSearch = () => {
    const trimmedSearch = searchValue.trim();
    if (!trimmedSearch) return;

    setShowSearchDropdown(false);

    // Global search navigation
    navigate(`/search?q=${encodeURIComponent(trimmedSearch)}`);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      triggerSearch();
    }
  };

  const handleResultClick = (type, item) => {
    setShowSearchDropdown(false);
    setSearchValue("");

    const routes = {
      'tickets': `/tickets/${item.id}`,
      'users': `/users/${item.id}`,
      'teams': `/team/${item.id}`,
      'sla': `/sla/${item.id}`,
      'articles': `/knowledge-system/article/${item.id}`,
      'notes': `/tickets/${item.ticket_id || ''}`
    };

    const path = routes[type];
    if (path) navigate(path);
  };

  // Route to title mapping (uses translation keys)
  const routeTitleMap = {
    '/': 'page.dashboard',
    '/dashboard': 'page.dashboard',
    '/tickets/dashboard': 'page.dashboard',
    '/tickets': 'page.helpdesk',
    '/compliance': 'page.compliance',
    '/contacts': 'page.contacts',
    '/settings': 'page.settings',
    '/users': 'page.users',
    '/company': 'page.company',
    '/profile': 'page.profile',
    '/sla': 'page.helpdesk',
    '/category': 'page.helpdesk',
    '/team': 'page.helpdesk',
    '/inbox': 'page.inbox',
    '/knowledge-system': 'page.knowledge_base',
    '/apps': 'page.app_store',
    '/marketing/config/templates': 'page.templates',
    '/marketing/campaigns': 'page.campaigns',
    '/marketing/contacts': 'page.audience',
    '/marketing': 'page.marketing',
    '/search': 'page.search',
    '/nexus': 'page.nexus_ai',
    '/nexus/agents': 'page.nexus_ai',
    '/nexus/agents/new': 'page.nexus_ai',
    '/nexus/skills': 'page.nexus_ai',
    '/nexus/teams': 'page.nexus_ai',
    '/nexus/tools': 'page.nexus_ai',
    '/nexus/vault': 'page.nexus_ai',
    '/nexus/approvals': 'page.nexus_ai',
    '/nexus/runs': 'page.nexus_ai',
    '/nexus/observability': 'page.nexus_ai',
    '/nexus/benchmark': 'page.nexus_ai',
    '/nexus/settings': 'page.nexus_ai',
    '/social-media': 'page.social_media',
    '/social-media/dashboard': 'page.social_media',
    '/social-media/posts': 'page.social_media',
    '/social-media/calendar': 'page.social_media',
    '/social-media/listening': 'page.social_media',
    '/social-media/connections': 'page.social_media',
    '/social-media/settings': 'page.social_media',
    '/social-media/crisis': 'page.social_media',
    '/sentiment': 'page.sentiment_intel',
    '/sentiment/dashboard': 'page.sentiment_intel',
    '/sentiment/journey': 'page.sentiment_intel',
    '/sentiment/coaching': 'page.sentiment_intel',
    '/sentiment/competitors': 'page.sentiment_intel',
    '/sentiment/briefing-history': 'page.sentiment_intel',
    '/sentiment/settings': 'page.sentiment_intel',
    '/appointments': 'page.appointments',
    '/appointments/working-hours': 'page.appointments',
    // Modules without a page.* key reuse their sidebar label.
    '/huddle': 'nav.huddle',
    '/voice': 'nav.voice',
    '/csat': 'nav.csat',
    '/crm': 'nav.crm',
    '/projects': 'nav.projects',
    '/calendar': 'nav.calendar',
    '/loyalty': 'nav.loyalty',
    '/admin/monitoring': 'nav.monitoring',
    '/integrations': 'nav.integrations',
    '/email/templates': 'nav.templates',
    '/email-logs': 'nav.email_logs',
    '/saas-admin': 'nav.saas_admin',
  };

  const toggleProfileDropdown = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen);
  };

  const { setToken } = useAuth();
  const handleLogout = async () => {
    setIsProfileDropdownOpen(false);
    // Hit the backend first (token still attached) so it records the LOGOUT
    // audit, deletes the server token and flips presence offline. Falls through
    // to local cleanup even if the network call fails.
    try { await apiLogout(); } catch { /* still clear local state below */ }
    setToken("");
    localStorage.removeItem("token");
    localStorage.removeItem("userdetails");
    localStorage.removeItem("active_company");
    localStorage.removeItem("tenant_schema");
    localStorage.removeItem("timezone");
    localStorage.removeItem("company_timezone");
    localStorage.removeItem("company_currency");
    navigate("/", { replace: true });
    window.location.reload(); // Ensure all state is cleared and app is reset
  };


  const getPageTitle = () => {
    let path = location.pathname;
    if (path.endsWith('/') && path.length > 1) {
      path = path.slice(0, -1);
    }

    if (path in routeTitleMap) {
      return routeTitleMap[path] ? t(routeTitleMap[path]) : '';
    }

    const sortedRoutes = Object.keys(routeTitleMap).sort((a, b) => b.length - a.length);
    // Match whole segments: '/crm' covers '/crm/leads' but not '/crmx'.
    const matchedRoute = sortedRoutes.find(route =>
      route !== '/' && path.startsWith(`${route}/`)
    );

    if (!matchedRoute) return t('page.dashboard');
    return routeTitleMap[matchedRoute] ? t(routeTitleMap[matchedRoute]) : '';
  };

  const pageTitle = getPageTitle();

  const { startTour } = useTour();
  const handleStartTour = () => {
    setIsProfileDropdownOpen(false);
    startTour('welcome', { force: true });
  };

  const handleProfile = () => {
    setIsProfileDropdownOpen(false);
    navigate("/profile", { replace: true });
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileDropdownOpen && !event.target.closest('.cx_header_user_profile')) {
        setIsProfileDropdownOpen(false);
      }
      if (isConfigMenuOpen && !event.target.closest('.cx_header_config_menu')) {
        setIsConfigMenuOpen(false);
      }
      if (showSearchDropdown && searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setShowSearchDropdown(false);
      }
      if (isQuickCreateOpen && quickCreateRef.current && !quickCreateRef.current.contains(event.target)) {
        setIsQuickCreateOpen(false);
      }
      if (isLangDropdownOpen && langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileDropdownOpen, isConfigMenuOpen, showSearchDropdown, isQuickCreateOpen, isLangDropdownOpen]);

  const hasResults = Object.values(searchResults).some(arr => arr && arr.length > 0);

  // No on the header: its background is an opaque bg-white, so the
  // blur had nothing to show through it and rendered no visible effect. What it
  // did do was promote the header to its own compositing layer and stacking
  // context, which is how it ended up interacting badly with the blurred scrim
  // a dialog paints over the page.
  return (
    <header className="cx-header w-full sticky top-0 z-[var(--cx-z-sticky)] bg-white border-b border-line transition-all duration-200">
      <div className="flex items-center justify-between p-[10px] max-w-full gap-4">
        {/* Left Section - Hamburger Menu, Back Arrow, Title */}
        <div className="flex items-center gap-1 min-w-0 flex-[0_0_auto]">
          <button
            className="hidden md:hidden items-center justify-center w-9 h-9 border-none bg-transparent rounded cursor-pointer transition-all duration-200 mr-1 hover:bg-gray-100 hover:text-gray-700 focus:outline-2 focus:outline-blue-600 focus:outline-offset-2 max-md:flex"
            onClick={() => window.dispatchEvent(new CustomEvent('toggle-sidebar'))}
            aria-label={t('header.toggle_menu')}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <button
            className="flex items-center justify-center w-8 h-8 border-none bg-transparent rounded cursor-pointer transition-all duration-200 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
            onClick={() => navigate(-1)}
            aria-label={t('header.go_back')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d={dir === 'rtl' ? "M9 18L15 12L9 6" : "M15 18L9 12L15 6"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {pageTitle && (
            <h1 className="text-[22px] font-semibold text-ink m-0 tracking-tight whitespace-nowrap leading-8 font-manrope">{pageTitle}</h1>
          )}
        </div>

        {/* Right Section - Search, Plus Button, Actions, and Profile */}
        <div className="cx-header-right flex items-center gap-4 flex-[0_0_auto]">
          <div className="cx-header-search-group flex items-center gap-3 flex-[0_0_auto]">
            {/* Search Container */}
            <div data-tour="header.search" className="cx-header-search relative flex-[0_0_auto] w-64" ref={searchContainerRef}>
              <div className={`relative flex items-center bg-white border ${showSearchDropdown ? 'border-blue-600 shadow-[0_0_0_3px_rgba(37,99,235,0.1)]' : 'border-line-2'} rounded-[20px] px-3 h-9 transition-all duration-200 shadow-[0px_1px_2px_0px_rgba(55,61,63,0.1)] focus-within:border-blue-600 focus-within:shadow-[0_0_0_3px_rgba(37,99,235,0.1)]`}>
                <svg
                  className={`shrink-0 w-5 h-5 transition-colors cursor-pointer ${dir === 'rtl' ? 'ml-2' : 'mr-2'} ${isSearching ? 'text-blue-500 animate-spin' : 'text-gray-400 opacity-50 hover:text-blue-500 hover:opacity-100'}`}
                  width="20" height="20" viewBox="0 0 24 24" fill="none"
                  onClick={triggerSearch}
                >
                  {isSearching ? (
                    <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  ) : (
                    <>
                      <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                      <path d="m21 21-4.35-4.35" stroke="currentColor" strokeWidth="2" />
                    </>
                  )}
                </svg>
                <input
                  type="text"
                  aria-label={t('header.search_placeholder')}
                  placeholder={t('header.search_placeholder')}
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={handleSearchKeyPress}
                  onFocus={() => searchValue.trim().length >= 2 && setShowSearchDropdown(true)}
                  className="flex-1 border-none bg-transparent outline-none py-2 text-sm text-[#212528] font-medium font-manrope leading-5 min-w-0 placeholder:text-text-4"
                />
              </div>

              {/* Global Search Dropdown */}
              {showSearchDropdown && searchValue.trim().length >= 2 && (
                <div className="absolute top-[calc(100%+8px)] left-0 right-0 bg-white border border-line rounded-xl shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1),0_8px_10px_-6px_rgba(0,0,0,0.1)] z-[var(--cx-z-modal)] overflow-hidden min-w-[320px] animate-in fade-in slide-in-from-top-2 duration-150">
                  {isSearching ? (
                    <div className="p-6 text-center">
                      <div className="inline-block w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></div>
                      <div className="text-sm text-gray-500 font-medium">{t('header.searching')}</div>
                    </div>
                  ) : !hasResults ? (
                    <div className="p-8 text-center">
                      <div className="text-gray-300 mb-2">
                        <svg className="mx-auto w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                      </div>
                      <div className="text-sm text-gray-500 font-medium">{t('header.no_results')} "{searchValue}"</div>
                    </div>
                  ) : (
                    <div className="max-h-[420px] overflow-y-auto py-2 custom-scrollbar">
                      {Object.entries(searchResults).map(([entityType, items]) => (
                        items && items.length > 0 && (
                          <div key={entityType} className="mb-2 last:mb-0">
                            <div className="px-4 py-1.5 text-xs font-extrabold uppercase text-text-4 tracking-[0.1em] bg-gray-50/50 flex items-center justify-between">
                              <span>{entityType}</span>
                              <span className="bg-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full text-xs">{items.length}</span>
                            </div>
                            <div className="px-1">
                              {items.map((item) => (
                                <div
                                  key={item.id}
                                  onClick={() => handleResultClick(entityType, item)}
                                  className="group flex items-center gap-3 px-3 py-2.5 hover:bg-blue-50/80 rounded-lg cursor-pointer transition-all duration-150 mx-1"
                                >
                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${entityType === 'tickets' ? 'bg-blue-100 text-blue-600' :
                                    entityType === 'users' ? 'bg-purple-100 text-purple-600' :
                                      entityType === 'teams' ? 'bg-orange-100 text-orange-600' :
                                        'bg-gray-100 text-gray-600'
                                    }`}>
                                    {entityType === 'tickets' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22l-10-10 10-10 10 10-10 10z" /></svg>}
                                    {entityType === 'users' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>}
                                    {entityType === 'teams' && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>}
                                    {!['tickets', 'users', 'teams'].includes(entityType) && <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-ink truncate group-hover:text-blue-700">{item.title}</div>
                                    <div className="text-xs text-text-4 font-medium flex items-center gap-2">
                                      {item.id && <span>ID: {item.id}</span>}
                                      {entityType === 'tickets' && <span className="w-1 h-1 bg-gray-300 rounded-full"></span>}
                                      {entityType === 'tickets' && <span>{t('page.helpdesk')}</span>}
                                    </div>
                                  </div>
                                  <svg className="opacity-0 group-hover:opacity-100 text-blue-500 transition-opacity" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                    <path strokeLinecap="round" strokeLinejoin="round" d={dir === 'rtl' ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                                  </svg>
                                </div>
                              ))}
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  )}
                  <div className="bg-gray-50 p-2.5 border-t border-gray-100 flex items-center justify-between">
                    <div className="text-xs text-gray-400 font-medium">{t('header.press_enter')}</div>
                    <div className="flex gap-1">
                      <span className="px-1.5 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-500 font-bold shadow-sm">ESC</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="relative" ref={quickCreateRef}>
              <button
                className={`relative border border-transparent rounded-lg w-9 h-9 flex items-center justify-center cursor-pointer transition-all duration-200 outline-none shrink-0 hover:-translate-y-px ${isQuickCreateOpen ? 'scale-95 brightness-90' : ''}`}
                style={{ background: 'linear-gradient(203.01deg, #1b76de 0%, #1766bf 100%)', boxShadow: '0px 1px 0px 0px rgba(94, 94, 94, 0.12), 0px 2px 6px 0px rgba(122, 122, 122, 0.12)' }}
                aria-label={t('header.create_new')}
                data-tour="header.create"
                onClick={() => setIsQuickCreateOpen(!isQuickCreateOpen)}
              >
                <svg
                  className={`text-white w-[18px] h-[18px] transition-transform duration-300 ${isQuickCreateOpen ? 'rotate-45' : ''}`}
                  width="18" height="18" viewBox="0 0 24 24" fill="none"
                >
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="absolute -bottom-[3px] left-1/2 -translate-x-1/2 w-[35px] h-3 bg-accent-light rounded-full" style={{ filter: 'blur(3px)' }}></div>
              </button>

              {/* Quick Create Dropdown */}
              {isQuickCreateOpen && (
                <div className={`absolute top-[calc(100%+12px)] ${dir === 'rtl' ? 'left-0' : 'right-0'} bg-white border border-line rounded-xl shadow-[0_15px_35px_-5px_rgba(0,0,0,0.15),0_10px_15px_-5px_rgba(0,0,0,0.05)] z-[var(--cx-z-modal)] overflow-hidden min-w-[240px] animate-in fade-in slide-in-from-top-2 zoom-in-95 duration-200`}>
                  <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="text-xs font-extrabold uppercase text-ink tracking-[0.1em] mb-0.5">{t('header.quick_create')}</h3>
                    <p className="text-xs text-gray-400 font-medium">{t('header.create_instantly')}</p>
                  </div>

                  <div className="p-2">
                    {/* CRM Section */}
                    <div className="mb-2">
                      <div className="px-3 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-widest">{t('quick_create.crm_relationships')}</div>
                      <button
                        onClick={() => { setIsQuickCreateOpen(false); navigate('/crm/leads/create'); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-green-50/80 rounded-lg group transition-colors ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center shrink-0">
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M12 4v16m8-8H4" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-ink group-hover:text-green-700">{t('quick_create.new_lead')}</div>
                          <div className="text-xs text-gray-400">{t('quick_create.new_lead_desc')}</div>
                        </div>
                      </button>
                      <button
                        onClick={() => { setIsQuickCreateOpen(false); navigate('/crm/accounts/create'); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50/80 rounded-lg group transition-colors ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h-2m-8 0H5m4 0h6m-3-13v4m0 0v4m0-4h-2m2 0h2" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-ink group-hover:text-blue-700">{t('quick_create.new_account')}</div>
                          <div className="text-xs text-gray-400">{t('quick_create.new_account_desc')}</div>
                        </div>
                      </button>
                      <button
                        onClick={() => { setIsQuickCreateOpen(false); navigate('/crm/opportunities/create'); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-amber-50/80 rounded-lg group transition-colors ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-600 flex items-center justify-center shrink-0">
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-ink group-hover:text-amber-700">{t('quick_create.new_deal')}</div>
                          <div className="text-xs text-gray-400">{t('quick_create.new_deal_desc')}</div>
                        </div>
                      </button>
                    </div>

                    {/* Helpdesk Section */}
                    <div className="mb-2">
                      <div className="px-3 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-widest">{t('quick_create.helpdesk')}</div>
                      <button
                        onClick={() => { setIsQuickCreateOpen(false); navigate('/tickets/create'); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50/80 rounded-lg group transition-colors ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 012-2h12a2 2 0 012 2v14a2 2 0 01-2 2H7a2 2 0 01-2-2V5z" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-ink group-hover:text-blue-700">{t('quick_create.new_ticket')}</div>
                          <div className="text-xs text-gray-400">{t('quick_create.new_ticket_desc')}</div>
                        </div>
                      </button>
                    </div>

                    {/* Knowledge Section */}
                    <div className="mb-2">
                      <div className="px-3 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-widest">{t('quick_create.knowledge_base')}</div>
                      <button
                        onClick={() => { setIsQuickCreateOpen(false); navigate('/knowledge-system'); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-purple-50/80 rounded-lg group transition-colors ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-ink group-hover:text-purple-700">{t('quick_create.new_article')}</div>
                          <div className="text-xs text-gray-400">{t('quick_create.new_article_desc')}</div>
                        </div>
                      </button>
                    </div>

                    {/* Management Section */}
                    <div>
                      <div className="px-3 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-widest">{t('quick_create.management')}</div>
                      <button
                        onClick={() => { setIsQuickCreateOpen(false); navigate('/users'); }}
                        className={`w-full flex items-center gap-3 px-3 py-2 hover:bg-orange-50/80 rounded-lg group transition-colors ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                      >
                        <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
                          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-ink group-hover:text-orange-700">{t('quick_create.invite_user')}</div>
                          <div className="text-xs text-gray-400">{t('quick_create.invite_user_desc')}</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Global Active Timer ─────────────────────────────────── */}
          <GlobalTimer />

          <div className="w-[1.1px] h-[22px] bg-line m-0"></div>

          <div className="flex items-center gap-0">
            {/* ── Notification Bell ──────────────────────────────────────── */}
            <div className="relative" ref={notifPanelRef}>
              <button
                className="relative flex items-center justify-center w-9 h-9 border border-white rounded-lg text-gray-500 cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:text-gray-700"
                aria-label="Notifications"
                data-tour="header.notifications"
                onClick={openNotifications}
              >
                <img src={bellIcon} alt="Notifications" className="w-5 h-5 object-contain" />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: 5, right: 5,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#ef4444', color: '#fff',
                    fontSize: 12, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '1.5px solid #fff', lineHeight: 1,
                  }}>
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {/* Notification Dropdown Panel */}
              {notifOpen && (
                <div style={{
                  position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                  width: 360, background: '#fff',
                  border: '1.5px solid #e6e9ef', borderRadius: 16,
                  boxShadow: '0 20px 48px rgba(0,0,0,0.12)',
                  zIndex: 9999, overflow: 'hidden',
                  animation: 'notifSlideDown 0.18s ease',
                  fontFamily: "'Manrope', sans-serif",
                }}>
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px 12px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 15 }}>🔔</span>
                      <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>Notifications</span>
                      {unreadCount > 0 && (
                        <span style={{ background: '#ef4444', color: '#fff', borderRadius: 999, padding: '1px 7px', fontSize: 12, fontWeight: 700 }}>{unreadCount}</span>
                      )}
                    </div>
                    <button
                      onClick={markAllRead}
                      style={{ fontSize: 12, fontWeight: 600, color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', padding: '3px 8px', borderRadius: 6, transition: 'background 0.1s' }}
                    >
                      Mark all read
                    </button>
                  </div>

                  {/* Body */}
                  <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    {notifLoading ? (
                      <div style={{ padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Loading…</div>
                    ) : notifications.length === 0 ? (
                      <div style={{ padding: 40, textAlign: 'center' }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>🔕</div>
                        <div style={{ fontSize: 13, color: '#94a3b8', fontWeight: 500 }}>You're all caught up!</div>
                      </div>
                    ) : (
                      notifications.map(n => (
                        <div
                          key={n.id}
                          onClick={() => handleNotifClick(n)}
                          style={{
                            display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 16px',
                            cursor: n.entity_url ? 'pointer' : 'default',
                            background: n.is_read ? '#fff' : '#f8f9ff',
                            borderBottom: '1px solid #f8fafc', transition: 'background 0.15s',
                          }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                          onMouseLeave={e => e.currentTarget.style.background = n.is_read ? '#fff' : '#f8f9ff'}
                        >
                          <div style={{ width: 34, height: 34, borderRadius: 10, background: '#f8fafc', border: `1.5px solid ${n.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>
                            {n.icon}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: n.is_read ? 500 : 700, color: '#1e293b', marginBottom: 2, lineHeight: 1.3 }}>{n.title}</div>
                            {n.body && <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5, marginBottom: 3 }}>{n.body}</div>}
                            <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 500 }}>{relativeTime(n.created_at)}</div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                            {!n.is_read && (
                              <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.color, marginTop: 3 }} />
                            )}
                            <button
                              onClick={e => dismiss(n.id, e)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1', fontSize: 12, padding: 2, borderRadius: 4, transition: 'color 0.1s' }}
                              title="Dismiss"
                            >✕</button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              <style>{`
                @keyframes notifSlideDown { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
              `}</style>
            </div>
            <button
              className={`flex items-center justify-center w-9 h-9 border border-white rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 ${isHelpOpen ? 'bg-gray-100 text-gray-700' : 'text-gray-500'}`}
              aria-label={t('header.help')}
              title={t('header.help')}
              data-tour="header.help"
              onClick={(e) => {
                e.stopPropagation();
                setIsHelpOpen(prev => !prev);
              }}
            >
              <img src={questionIcon} alt={t('header.help')} className="w-5 h-5 object-contain pointer-events-none" />
            </button>

            {/* Language Switcher */}
            <div className="relative" ref={langDropdownRef}>
              <button
                className={`flex items-center justify-center w-9 h-9 border border-white rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-100 hover:text-gray-700 ${isLangDropdownOpen ? 'bg-gray-100 text-gray-700' : 'text-gray-500'}`}
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                aria-label={t('header.language')}
                title={t('header.language')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M2 12h20" />
                  <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                </svg>
              </button>

              {isLangDropdownOpen && (
                <div className={`absolute top-[calc(100%+8px)] ${dir === 'rtl' ? 'left-0' : 'right-0'} bg-white border border-gray-200 rounded-lg min-w-[160px] py-1.5 z-[var(--cx-z-dropdown)] animate-in fade-in slide-in-from-top-2 duration-150`} style={{ boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
                  <div className="px-3 py-1.5 text-xs font-bold text-gray-400 uppercase tracking-widest">{t('header.language')}</div>
                  {Object.entries(languages).map(([code, config]) => (
                    <button
                      key={code}
                      onClick={() => { setLanguage(code); setIsLangDropdownOpen(false); }}
                      className={`flex items-center gap-2.5 w-full py-2 px-3 border-none bg-transparent cursor-pointer text-sm font-medium tracking-tight transition-all duration-200 hover:bg-gray-50 ${language === code ? 'text-blue-600' : 'text-gray-700'} ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                    >
                      <span className="text-base">{config.flag}</span>
                      <span>{config.nativeLabel}</span>
                      {language === code && (
                        <svg className={`w-4 h-4 text-blue-600 ${dir === 'rtl' ? 'mr-auto' : 'ml-auto'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {isAppInstalled('admin_monitoring') && <StatusSelector />}

          <div data-tour="header.profile" className="relative cx_header_user_profile">
            <button
              className="flex items-center gap-[5px] p-0 border-none bg-transparent rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-100"
              onClick={toggleProfileDropdown}
              aria-expanded={isProfileDropdownOpen}
            >
              <div className="flex items-center gap-2">
                <img
                  src={userdetails.profile_image || `https://ui-avatars.com/api/?name=${userdetails.firstname}+${userdetails.lastname}&background=2563eb&color=fff&size=28`}
                  alt={`${userdetails.firstname} ${userdetails.lastname}`}
                  className="w-7 h-7 rounded-full object-cover"
                />
                <div className={dir === 'rtl' ? 'text-right min-w-0' : 'text-left min-w-0'}>
                  <div className="text-xs font-semibold text-ink leading-tight tracking-tight whitespace-nowrap font-manrope">{userdetails.firstname} {userdetails.lastname}</div>
                  <div className="text-xs font-medium text-text-4 leading-tight whitespace-nowrap font-manrope w-21">
                    {userdetails.is_superuser ? 'Super Admin' : userdetails.is_staff ? 'Staff' : 'Standard User'}
                  </div>
                </div>
              </div>
              <img src={dropdownIcon} alt="Dropdown" className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isProfileDropdownOpen && (
              <div className={`absolute top-[calc(100%+8px)] ${dir === 'rtl' ? 'left-0' : 'right-0'} bg-white border border-gray-200 rounded-lg min-w-[180px] py-2 z-[var(--cx-z-dropdown)] animate-in fade-in slide-in-from-top-2 duration-150`} style={{ boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}>
                <button onClick={handleProfile} className={`flex items-center gap-3 w-full py-3 px-4 border-none bg-transparent cursor-pointer text-sm font-medium text-gray-700 tracking-tight transition-all duration-200 hover:bg-gray-100 hover:text-gray-900 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                  <svg className="text-gray-500 shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke="currentColor" strokeWidth="2" /><circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span>{t('header.profile')}</span>
                </button>
                <button onClick={handleStartTour} className={`flex items-center gap-3 w-full py-3 px-4 border-none bg-transparent cursor-pointer text-sm font-medium text-gray-700 tracking-tight transition-all duration-200 hover:bg-gray-100 hover:text-gray-900 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                  <svg className="text-gray-500 shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" /><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                  <span>{t('tour.menu_product_tour')}</span>
                </button>
                <button onClick={handleLogout} className={`flex items-center gap-3 w-full py-3 px-4 border-none bg-transparent cursor-pointer text-sm font-medium text-gray-700 tracking-tight transition-all duration-200 hover:bg-gray-100 hover:text-gray-900 ${dir === 'rtl' ? 'text-right' : 'text-left'}`}>
                  <svg className="text-gray-500 shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="2" /><polyline points="16,17 21,12 16,7" stroke="currentColor" strokeWidth="2" /><line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  <span>{t('header.logout')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <HelpCenter
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        currentRoute={location.pathname}
      />
    </header>
  );
};

export default Header;